import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "src/store/store";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {
  addNewField,
  removeField,
  addNewModel,
  getModels,
  setModelName,
  validateAndUpsertModel,
  ModelStatus,
  importModelFromJSON,
} from "src/store/modelManagementSlice";
import ValidationErrorModal from "src/components/pages/modelManagement/ValidationErrorModal";
import Input from "@amzn/meridian/input";
import { DUMMY_MODEL_ID } from "src/constants";
import { isFulfilled, isRejected } from "@reduxjs/toolkit";
import ScrollableSection from "src/components/shared/scrollableSection/scrollableSection";
import TitleAndButton from "src/components/shared/titleAndButton/titleAndButton";
import ModelRecords from "src/components/pages/modelManagement/modelRecords";
import FieldRecords from "src/components/pages/modelManagement/fieldRecords";
import FieldDetail from "src/components/pages/modelManagement/fieldDetail";
import Text from "@amzn/meridian/text";
import { isAdminUser, isProd } from "src/utils/permissionUtil";
import { createToast } from "src/store/toastsSlice";
import { TOAST_TIMEOUT } from "src/config/Toast";
import FileInput from "@amzn/meridian/file-input";

const ModelManagement = () => {
  const dispatch = useAppDispatch();
  const {
    selectedModel,
    selectedModelId,
    selectedFieldId,
    isCreateModel,
    isCreatingField,
    errors,
  } = useAppSelector((state) => state.modelSlice);
  const { ldap } = useAppSelector((state) => state.user);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    setShowErrorModal(false);
  }, [selectedModelId]);

  return (
    <Row
      spacingInset={"500 500 0 500"}
      alignmentVertical="top"
      widths={["grid-3", "fit", "grid-3", "fit", "fill"]}
      className="model-management"
      overflowX="hidden"
      overflowY="hidden"
    >
      <ScrollableSection>
        <TitleAndButton
          title="Model"
          disabled={isCreateModel || (!isAdminUser(ldap) && isProd())}
          onClick={() => {
            dispatch(addNewModel());
          }}
        ></TitleAndButton>
        {!isCreateModel && <ModelRecords></ModelRecords>}
        {isCreateModel && selectedModelId === DUMMY_MODEL_ID && (
          <>
            <Row widths={"fill"} alignmentHorizontal={"start"}>
              <Input
                value={selectedModel.name}
                onChange={(name) => dispatch(setModelName(name))}
                type="text"
                placeholder="Enter Model Name..."
                label="Model Name"
              />
            </Row>
            <Row alignmentHorizontal={"end"}>
              <FileInput
                type="single"
                accept=".json"
                uploadButtonType="secondary"
                uploadButtonLabel="Import model"
                onFileAttached={(acceptedFiles) => {
                  if (acceptedFiles[0]) {
                    dispatch(importModelFromJSON(acceptedFiles[0]))
                      .unwrap()
                      .then(() => {
                        dispatch(
                          createToast({
                            type: "success",
                            message: "Model imported successfully",
                            timeout: TOAST_TIMEOUT,
                          }),
                        );
                      })
                      .catch((error) => {
                        dispatch(
                          createToast({
                            type: "error",
                            message: `Failed to import model: ${error.message}`,
                            timeout: TOAST_TIMEOUT,
                          }),
                        );
                      });
                  }
                }}
              />
            </Row>
          </>
        )}
      </ScrollableSection>
      <div className="split-line"></div>
      <ScrollableSection>
        <TitleAndButton
          title="Field"
          disabled={
            selectedModelId == "" ||
            isCreatingField ||
            selectedModel.status === ModelStatus.inactive
          }
          onClick={() => {
            dispatch(addNewField());
          }}
        ></TitleAndButton>
        {<FieldRecords></FieldRecords>}
      </ScrollableSection>
      <div className="split-line"></div>
      <ScrollableSection>
        <Row widths={["fill", "grid-2", "grid-1"]}>
          <Column></Column>
          <Button
            size="small"
            type="tertiary"
            disabled={
              selectedFieldId === "" ||
              selectedModel.status === ModelStatus.inactive ||
              selectedModel.isRagEnabled
            }
            onClick={() => dispatch(removeField())}
          >
            Delete
          </Button>
          <Column></Column>
        </Row>
        {selectedFieldId && <FieldDetail></FieldDetail>}
        {selectedFieldId === "" ? (
          <Column height={"60%"} alignmentVertical={"center"} alignmentHorizontal={"center"}>
            <Text>Please select your field</Text>
          </Column>
        ) : (
          <Row spacingInset={"none none none 400"} alignmentHorizontal={"start"}>
            <Button
              onClick={async () => {
                const result = await dispatch(validateAndUpsertModel(selectedModel));
                if (isFulfilled(result)) {
                  dispatch(getModels());
                } else if (isRejected(result) && result.meta.rejectedWithValue) {
                  setShowErrorModal(true);
                } else if (isRejected(result)) {
                  dispatch(
                    createToast({
                      type: "error",
                      message: "Failed to save model. Please try again.",
                      timeout: TOAST_TIMEOUT,
                    }),
                  );
                }
              }}
            >
              Save
            </Button>
          </Row>
        )}
      </ScrollableSection>
      <ValidationErrorModal
        open={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        errors={errors}
        model={selectedModel}
      />
    </Row>
  );
};

export default ModelManagement;
